import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';

export default function CareSpotlightSection({ onApplyWhatsApp, onViewCatalogue }) {
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  const closeVideo = useCallback(() => setIsVideoOpen(false), []);

  useEffect(() => {
    if (!isVideoOpen) return undefined;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeVideo();
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [closeVideo, isVideoOpen]);

  return (
    <section className="care-spotlight" id="care">
      <div className="care-spotlight-inner">
        <motion.div
          className="care-spotlight-copy"
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="care-topline" aria-hidden="true">
            Health records available <span>•</span> Gently socialized <span>•</span> Guided handoff <span>•</span>{' '}
            Nationwide pickup
          </div>
          <div className="care-kicker">Raised with exceptional care</div>
          <h2 className="care-headline">Nurtured from day one.</h2>
          <p>
            These tiny babies are full of sweetness and already showing the most peaceful, cuddly little personalities
            as they nurse beside their mama. We’re opening applications for loving homes and would love your help
            choosing their names.
          </p>
          <p className="care-note">
            When you apply, include the name you’d like to give your favorite baby — we can’t wait to see your cutest
            name ideas and match these little ones with their forever families.
          </p>
          <div className="care-actions">
            <button className="care-primary" type="button" onClick={onViewCatalogue}>
              View Available Pups
            </button>
            <button className="care-secondary" type="button" onClick={onApplyWhatsApp}>
              Apply via WhatsApp
            </button>
          </div>
          <div className="care-tagline">Calm beginnings create confident companions.</div>
        </motion.div>

        <motion.div
          className="care-spotlight-media"
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.05 }}
        >
          <motion.div
            className="care-video-frame"
            whileHover={{ y: -6, boxShadow: '0 50px 140px rgba(15, 23, 42, 0.22)' }}
            transition={{ duration: 0.25 }}
            role="button"
            tabIndex={0}
            aria-label="Open the nursery video in a larger view"
            onClick={() => setIsVideoOpen(true)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') setIsVideoOpen(true);
            }}
          >
            <video src="/videos/story/motherpup.mp4" autoPlay muted playsInline loop preload="auto" />
            <div className="care-video-hint" aria-hidden="true">
              Tap to enlarge
            </div>
          </motion.div>
          <div className="care-stills" aria-hidden="true">
            {['/images/story/motherpup-01.jpeg', '/images/story/motherpup-02.jpeg'].map((src, idx) => (
              <motion.div
                key={src}
                className={`care-still care-still-${idx + 1}`}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.12 + idx * 0.06 }}
                whileHover={{ y: -4 }}
                style={{ willChange: 'transform' }}
              >
                <img src={src} alt="" loading="lazy" />
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {isVideoOpen ? (
        <div className="care-video-modal" role="dialog" aria-modal="true" aria-label="Nursery video">
          <button className="care-video-backdrop" type="button" aria-label="Close video" onClick={closeVideo} />
          <div className="care-video-modal-card" role="document">
            <button className="care-video-close" type="button" onClick={closeVideo} aria-label="Close video">
              ×
            </button>
            <video src="/videos/story/motherpup.mp4" autoPlay muted playsInline loop preload="auto" controls />
          </div>
        </div>
      ) : null}
    </section>
  );
}
